import is from '@sindresorhus/is';

import type { SkillShort } from '~/shared/data-contract/skill';

import { type LearningOpportunity, type LearningOpportunityCreate, normalizeUrlInput } from './data-contract';
import { mapSkillToLearningOpportunityPayload } from './helpers';

export type SidepanelMode = 'create' | 'edit' | 'view';

export type LearningOpportunityFormState = Omit<LearningOpportunityCreate, 'skills'> & {
    readonly ownerName?: string;
    readonly skills: readonly SkillShort[];
};

const emptyTranslation = (language: string): LearningOpportunityFormState['translations'][number] => ({
    description: '',
    language,
    title: '',
});

export const mergeTranslationsWithLanguages = (
    translations: LearningOpportunityFormState['translations'],
    languages: readonly string[],
): LearningOpportunityFormState['translations'] => {
    const existingByLanguage = new Map(translations.map((translation) => [translation.language, translation]));
    const mergedTranslations = languages.map(
        (language) => existingByLanguage.get(language) ?? emptyTranslation(language),
    );
    const knownLanguages = new Set(languages);
    const additionalTranslations = translations.filter((translation) => !knownLanguages.has(translation.language));

    return [...mergedTranslations, ...additionalTranslations];
};

export const mapOpportunityToFormState = (
    opportunity: LearningOpportunity,
    ownerName: string,
    languages: readonly string[],
): LearningOpportunityFormState => ({
    custom: opportunity.custom ?? {},
    learningLanguage: opportunity.learningLanguage,
    ownerName: opportunity.ownerName ?? ownerName,
    provider: opportunity.provider,
    skills: opportunity.skills ?? [],
    state: opportunity.state,
    translations: mergeTranslationsWithLanguages(opportunity.translations, languages),
    type: opportunity.type,
    url: opportunity.url ?? '',
});

export const createEmptyFormState = (
    ownerName: string,
    languages: readonly string[],
): LearningOpportunityFormState => ({
    custom: {},
    learningLanguage: undefined,
    ownerName,
    provider: undefined,
    skills: [],
    state: 'draft',
    translations: languages.map(emptyTranslation),
    type: 'course',
    url: '',
});

export const getLocalizedTranslationIndex = (state: LearningOpportunityFormState, languageCode: string): number => {
    const index = state.translations.findIndex((translation) => translation.language === languageCode);

    return index >= 0 ? index : 0;
};

const hasTranslationText = (translation: LearningOpportunityFormState['translations'][number]): boolean =>
    is.nonEmptyStringAndNotWhitespace(translation.title) || is.nonEmptyStringAndNotWhitespace(translation.description);

// View mode shows the resolved course title when the selected translation has none,
// mirroring the description fallback. Editing keeps the field empty so the placeholder shows.
export const getDisplayedTitle = (
    translationTitle: string | undefined,
    fallbackTitle: string | undefined,
    isEditing: boolean,
): string => {
    const title = translationTitle ?? '';
    if (isEditing || is.nonEmptyStringAndNotWhitespace(title)) return title;

    return fallbackTitle ?? '';
};

export const getTextLanguages = (translations: LearningOpportunityFormState['translations']): readonly string[] =>
    translations.filter(hasTranslationText).map((translation) => translation.language);

// Languages holding the original text while the selected translation is still empty.
export const getOriginLanguages = (textLanguages: readonly string[], activeLanguage: string): readonly string[] =>
    textLanguages.includes(activeLanguage) ? [] : textLanguages;

// Tenant UI languages keep their order; languages that only exist as saved text are appended.
export const getPickerLanguages = (
    tenantLanguages: readonly string[],
    textLanguages: readonly string[],
): readonly string[] => [...new Set([...tenantLanguages, ...textLanguages])];

export const TITLE_MIN_LENGTH = 3;
export const TITLE_MAX_LENGTH = 255;

const isSentTranslation = (translation: LearningOpportunityFormState['translations'][number]): boolean =>
    is.nonEmptyString(translation.title) || is.nonEmptyString(translation.description);

const hasValidTitleLength = (title: string): boolean =>
    title.length >= TITLE_MIN_LENGTH && title.length <= TITLE_MAX_LENGTH;

// The backend requires 3 to 255 title characters on every translation that is sent.
// While nothing is sent yet, the translation being edited needs a title.
export const isTranslationTitleValid = (
    translations: LearningOpportunityFormState['translations'],
    index: number,
): boolean => {
    const translation = translations[index];
    if (translation === undefined) return true;
    const mustHaveTitle = isSentTranslation(translation) || !translations.some(isSentTranslation);

    return !mustHaveTitle || hasValidTitleLength(translation.title);
};

export const findInvalidTitle = (
    translations: LearningOpportunityFormState['translations'],
):
    | { readonly index: number; readonly translation: LearningOpportunityFormState['translations'][number] }
    | undefined => {
    const index = translations.findIndex(
        (translation) => isSentTranslation(translation) && !hasValidTitleLength(translation.title),
    );
    const translation = translations[index];

    return translation === undefined ? undefined : { index, translation };
};

export const submitFormPayload = (
    value: LearningOpportunityFormState,
    onSubmit: (value: LearningOpportunityCreate) => void,
): void => {
    const { ownerName: _ownerName, ...payload } = value;
    onSubmit({
        ...payload,
        skills: payload.skills.map(mapSkillToLearningOpportunityPayload),
        translations: payload.translations.filter(isSentTranslation),
        url: normalizeUrlInput(payload.url ?? ''),
    });
};
